import { getApiClientErrorMessageFromError, hydraUiApi } from '@/services/api';
import { axiosServicesApi as untypedServicesApi } from '@/services/api/deprecated';

import { AiEnabledGuard } from '@/components/guards';
import { authStore } from '@/services/auth/store';
import { getApiErrorBody } from '@/utils/errors';
import {
  CopilotActiveExchangeError,
  setCopilotConfig,
  type APICopilotGetFileDownloadUrl,
  type APICopilotGetUploadSignature,
  type CopilotMessage,
  type CopilotUploadSignatureResponse,
  type ServerSideCopilotToolPayload,
} from '@customerio/hydra-copilot';
import { SidebarChat } from './chat';
import { History } from './history';
import { getXhrResponseText } from './helpers/get-xhr-response-text';
import { processStreamingLines } from './helpers/process-streaming-lines';
import { useIsAgentFullscreen } from './hooks';
import { resolveCopilotWorkspaceId } from './workspace-context';

setCopilotConfig({
  async startSession() {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await hydraUiApi.copilot.create({
      params: { path: { account_id: String(accountId) } },
    });

    return {
      id: String(response.session_id),
      // `tools` is modeled as an untyped JSON array in the spec; narrow it to
      // the server-side tool payload shape the copilot package expects.
      tools: response.tools as ServerSideCopilotToolPayload[],
    };
  },
  getUploadSignature: (async (
    payload: Parameters<APICopilotGetUploadSignature>[0],
  ): Promise<CopilotUploadSignatureResponse> => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    try {
      const response = await hydraUiApi.copilot.sign({
        params: {
          path: {
            account_id: String(accountId),
            session_id: payload.id,
          },
        },
        body: {
          size: payload.size,
          mime_type: payload.mime_type,
        },
      });

      // The signed-upload shape is identical at runtime; the generated GCS
      // signature type is looser (optional/nullable map) than the copilot
      // package's, so narrow it at this boundary.
      return response as CopilotUploadSignatureResponse;
    } catch (error) {
      const bodyMessage = getApiClientErrorMessageFromError(error);
      if (bodyMessage != null) {
        throw new Error(bodyMessage, { cause: error });
      }
      throw error;
    }
  }) satisfies APICopilotGetUploadSignature,
  getFileDownloadUrl: (async (payload: Parameters<APICopilotGetFileDownloadUrl>[0]) => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await hydraUiApi.copilot.getFiles({
      params: {
        path: {
          account_id: String(accountId),
          session_id: payload.id,
          filename: payload.filename,
        },
      },
    });

    return {
      url: response.url,
    };
  }) satisfies APICopilotGetFileDownloadUrl,
  async getSession(id) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await hydraUiApi.copilot.get({
      params: { path: { account_id: String(accountId), session_id: String(id) } },
    });

    return {
      meta: { name: response.name },
      // Generated message/tool models are structurally looser than the copilot
      // package's union types; narrow them at this boundary.
      messages: response.messages as CopilotMessage[],
      tools: response.tools as ServerSideCopilotToolPayload[],
    };
  },
  async sendStreamingMessage({ onMessage, id, signal, ...payload }) {
    const state = { currentLine: -1 };
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    try {
      const result = await untypedServicesApi.post(
        `/accounts/${accountId}/copilot/${id}`,
        {
          environment_id: resolveCopilotWorkspaceId(),
          stream: true,
          ...payload,
        },
        {
          signal,
          onDownloadProgress: (progressEvent) => {
            const response = getXhrResponseText(progressEvent);
            if (response != null) {
              processStreamingLines(response, state, onMessage);
            }
          },
        },
      );

      // axios throttles onDownloadProgress and can drop the final flush, which
      // carries the trailing `meta` message. Reconcile against the complete body
      // so the stream is never seen as interrupted when it actually finished.
      if (typeof result.data === 'string') {
        processStreamingLines(result.data, state, onMessage);
      }
    } catch (error) {
      if (getErrorResponseStatus(error) === 409) {
        throw new CopilotActiveExchangeError('The agent is still working on the previous turn.', { cause: error });
      }

      // Only rewrap when there's a body detail to surface; pass cancels,
      // timeouts, network failures through unchanged so downstream
      // isAbortError / retry logic still works.
      const bodyMessage = getApiErrorBody(error);
      if (bodyMessage != null) {
        throw new Error(bodyMessage, { cause: error });
      }
      throw error;
    }
  },
  async stopExchange({ id }) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await untypedServicesApi.post(`/accounts/${accountId}/copilot/${id}/stop`, {});
  },
  async endSession() {
    // noop
  },
  async generate(payload) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const { data } = await untypedServicesApi.post(`/accounts/${accountId}/copilot_generate`, {
      ...payload,
      environment_id: resolveCopilotWorkspaceId(),
    });

    return data;
  },
});

function getErrorResponseStatus(error: unknown): number | undefined {
  if (error == null || typeof error !== 'object') {
    return undefined;
  }

  const response = 'response' in error ? error.response : undefined;
  return response != null && typeof response === 'object' && 'status' in response && typeof response.status === 'number'
    ? response.status
    : undefined;
}

export function Copilot() {
  const isAgentFullscreen = useIsAgentFullscreen();
  if (isAgentFullscreen) {
    return null;
  }

  return (
    <>
      <AiEnabledGuard>
        <History />
      </AiEnabledGuard>
      <SidebarChat />
    </>
  );
}

export { CopilotInput, CopilotMessages, CopilotName, CopilotSupportButton } from './chat';
export { AgentBetaLabel } from './components/agent-beta-label';
export { AiGradientSparkleBadge } from './components/ai-gradient-sparkle-badge';
export { AiGradientSparkleIcon } from './components/ai-gradient-sparkle-icon';
export { CopilotSessionProvider, useCopilotSessionContext } from './components/session-provider';
export { AssistantFooter } from './components/assistant-footer';
export { CopilotSidebarProvider, useCopilotSidebar, useIsAgentFullscreen, useNavigateToAgentChat } from './hooks';
export { Chats as CopilotChats, ChatCountBar as CopilotChatCountBar } from './history';
export { useCopilotHistory } from './hooks';
export { useMasqAccessLevel } from './hooks/use-masq-access-level';
export { GlobalCopilotConfiguration } from './global-copilot-configuration';
